import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { motion } from 'framer-motion';
import ParticleBackground from '../components/ParticleBackground';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

export default function Home({ spreads, errors, meta_tags }) {
  const [question, setQuestion] = useState('');
  const [selectedSpread, setSelectedSpread] = useState('three-card-spread');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(errors?.question || '');
  const [isThinking, setIsThinking] = useState(false);

  const displayError = error || errors?.question || errors?.spread || '';
  const isLoading = isThinking && !errors?.question;
  const isDisabled = isSubmitting && !errors?.question;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!question.trim()) {
      return setError('Please enter a question.');
    }
    if (question.trim().length < 10) {
      return setError('Question is too short. Please be more specific.');
    }

    setIsThinking(true);
    setIsSubmitting(true);

    // Show thinking animation for at least 1.5 seconds
    await new Promise(resolve => setTimeout(resolve, 1500));

    router.post('/readings', {
      reading: {
        question: question,
        spread_slug: selectedSpread
      }
    }, {
      onError: () => {
        setIsThinking(false);
        setIsSubmitting(false);
        setError('Something went wrong. Please try again.');
      },
      onSuccess: () => {
        setIsThinking(false);
      }
    });
  };

  const popularSpreads = spreads.filter(s =>
    ['three-card-spread', 'one-card-draw', 'relationship-spread'].includes(s.slug)
  );

  return (
    <>
      <Seo meta={meta_tags} />

      <div className="bg-gradient-to-b from-indigo-950 via-purple-900 to-indigo-950 min-h-screen relative overflow-hidden flex flex-col">
        <ParticleBackground />
        <Header />

      <div className="relative z-10 flex-1">
        {/* Hero Section */}
        <section className="pt-20 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Animated Icon */}
              <motion.div
                className="inline-block mb-6"
                animate={{
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <div className="relative">
                  <span className="text-7xl drop-shadow-[0_0_20px_rgba(251,191,36,0.5)]">🔮</span>
                  {/* Glow effect */}
                  <motion.div
                    className="absolute inset-0 blur-xl bg-amber-500/30 rounded-full"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                  />
                </div>
              </motion.div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6">
                <span className="bg-gradient-to-r from-amber-200 via-purple-300 to-pink-300 bg-clip-text text-transparent drop-shadow-lg">
                  Free AI Tarot Reading
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-purple-100 mb-4 max-w-3xl mx-auto leading-relaxed">
                Seek insights and guidance on life's questions. Let our AI-powered tarot reader illuminate your path.
              </p>
              <div className="flex items-center justify-center gap-3 text-amber-200">
                <span className="text-2xl">✨</span>
                <p className="text-lg font-semibold">
                  100% Free • No Signup Required • Instant Results
                </p>
                <span className="text-2xl">✨</span>
              </div>
            </motion.div>

            {/* Question Form */}
            <motion.div
              className="max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                {/* Glowing border effect */}
                <motion.div
                  className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 rounded-3xl blur opacity-30"
                  animate={{
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                  }}
                />

                <div className="relative bg-gradient-to-br from-purple-900/90 to-indigo-900/90 backdrop-blur-xl p-8 md:p-10 rounded-3xl border-2 border-purple-400/30 shadow-2xl">
                  <div className="text-center mb-6">
                    <h2 className="text-3xl font-heading font-bold text-white mb-2 flex items-center justify-center gap-3">
                      <span>🎴</span>
                      Begin Your Journey
                      <span>🎴</span>
                    </h2>
                    <p className="text-purple-200">
                      Ask your question and let the cosmos guide you
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Question Input */}
                  <div>
                    <label htmlFor="question" className="block text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <span>🔮</span>
                      What question weighs on your heart?
                    </label>
                    <textarea
                      id="question"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      rows={4}
                      className="w-full px-5 py-4 bg-indigo-950/50 border-2 border-purple-400/30 rounded-xl text-white text-lg placeholder-purple-300/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 resize-none transition-all"
                      placeholder="What do I need to know about my current situation?&#10;&#10;What should I focus on this week?&#10;What is blocking my progress?"
                      required
                      disabled={isDisabled}
                    />
                    <p className="mt-3 text-sm text-purple-200 flex items-center gap-2">
                      <span>💫</span>
                      Open-ended questions work best for deeper insights
                    </p>
                  </div>

                  {/* Spread Selection */}
                  <div>
                    <label className="block text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <span>🎴</span>
                      Choose Your Spread
                    </label>
                    <div className="grid gap-3">
                      {popularSpreads.map((spread) => (
                        <motion.label
                          key={spread.slug}
                          className={`flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            selectedSpread === spread.slug
                              ? 'border-amber-400 bg-amber-500/10'
                              : 'border-purple-400/30 hover:border-amber-400/50 hover:bg-purple-500/10'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <input
                            type="radio"
                            name="spread"
                            value={spread.slug}
                            checked={selectedSpread === spread.slug}
                            onChange={(e) => setSelectedSpread(e.target.value)}
                            className="mt-1 text-amber-500 focus:ring-amber-500"
                            disabled={isDisabled}
                          />
                          <div className="ml-3 flex-1">
                            <div className="font-semibold text-white">
                              {spread.name}
                              <span className="text-sm text-purple-300 ml-2">({spread.card_count} cards)</span>
                            </div>
                            <p className="text-sm text-purple-200 mt-1">{spread.description}</p>
                          </div>
                        </motion.label>
                      ))}
                    </div>
                    <div className="mt-3 text-center">
                      <a href="#all-spreads" className="text-sm text-amber-300 hover:text-amber-200 flex items-center justify-center gap-2">
                        View all {spreads.length} spread options
                        <span>→</span>
                      </a>
                    </div>
                  </div>

                  {/* Error Message */}
                  {displayError && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-4 bg-red-900/30 border-2 border-red-400 rounded-xl text-red-200 text-center"
                    >
                      {displayError}
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isDisabled}
                    className="w-full relative overflow-hidden bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 text-white px-8 py-5 rounded-full text-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    whileHover={!isDisabled ? {
                      scale: 1.02,
                      boxShadow: '0 0 40px rgba(251, 191, 36, 0.8)'
                    } : {}}
                    whileTap={!isDisabled ? { scale: 0.98 } : {}}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-amber-400 via-purple-400 to-pink-400"
                      animate={{
                        x: ['-100%', '100%'],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                      style={{ opacity: 0.3 }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      {isLoading ? (
                        <>
                          <motion.span
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          >
                            🔮
                          </motion.span>
                          Channeling the cosmos...
                        </>
                      ) : (
                        <>
                          ✨ Start Your Reading ✨
                        </>
                      )}
                    </span>
                  </motion.button>

                  <p className="text-center text-purple-300/70 text-xs mt-6">
                    🌟 100% Free • No Signup Required • Instant AI Interpretation 🌟
                  </p>
                </form>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* All Spreads Section */}
        <section id="all-spreads" className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-3">
                Explore All Tarot Spreads
              </h2>
              <p className="text-purple-200 flex items-center justify-center gap-2">
                <span>🎴</span>
                Choose the perfect spread for your question
                <span>🎴</span>
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {spreads.map((spread, index) => (
                <motion.div
                  key={spread.slug}
                  className="relative group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  {/* Card glow on hover */}
                  <motion.div
                    className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-purple-500 rounded-xl opacity-0 group-hover:opacity-50 blur transition-opacity"
                  />

                  <a
                    href={`/readings/${spread.slug}`}
                    className="relative block p-6 bg-purple-900/40 backdrop-blur rounded-xl border border-purple-400/20 group-hover:border-amber-400/50 transition-all"
                  >
                    <h3 className="text-xl font-heading font-semibold mb-2 text-white group-hover:text-amber-300 transition-colors">
                      {spread.name}
                      <span className="text-sm text-purple-300 ml-2">({spread.card_count} cards)</span>
                    </h3>
                    <p className="text-purple-200 text-sm mb-4">{spread.description}</p>
                    <div className="text-amber-400 text-sm font-semibold flex items-center gap-2">
                      Learn more
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tarot Card Meanings */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-3">
              Explore All 78 Tarot Card Meanings
            </h2>
            <p className="text-purple-200 mb-8 max-w-2xl mx-auto">
              Learn the upright and reversed meaning of every tarot card — the 22 Major Arcana and
              the 56 Minor Arcana across Wands, Cups, Swords and Pentacles.
            </p>
            <a
              href="/cards"
              className="inline-flex items-center gap-2 bg-purple-800/50 text-purple-100 px-8 py-4 rounded-full text-lg font-bold border-2 border-purple-400/30 hover:border-amber-300 hover:text-amber-200 transition-all"
            >
              <span>🎴</span>
              Browse Tarot Card Meanings
              <span>→</span>
            </a>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 bg-gradient-to-b from-transparent via-purple-900/20 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-3">
                How It Works
              </h2>
              <p className="text-purple-200">
                Three simple steps to unlock cosmic wisdom
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-purple-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <span className="text-4xl">📝</span>
                  </div>
                  <motion.div
                    className="absolute inset-0 bg-amber-500/30 rounded-full blur-xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                  />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3 text-white">1. Ask Your Question</h3>
                <p className="text-purple-200">
                  Focus your intention and ask a question that's on your mind. Be specific for the best guidance.
                </p>
              </motion.div>

              <motion.div
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-purple-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <span className="text-4xl">🎴</span>
                  </div>
                  <motion.div
                    className="absolute inset-0 bg-purple-500/30 rounded-full blur-xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: 0.5,
                    }}
                  />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3 text-white">2. Choose Your Cards</h3>
                <p className="text-purple-200">
                  Watch the cards shuffle and select the ones that call to you. Trust your intuition.
                </p>
              </motion.div>

              <motion.div
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-purple-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <span className="text-4xl">🔮</span>
                  </div>
                  <motion.div
                    className="absolute inset-0 bg-pink-500/30 rounded-full blur-xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: 1,
                    }}
                  />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3 text-white">3. Receive AI Guidance</h3>
                <p className="text-purple-200">
                  Our advanced AI interprets your cards and provides personalized insights and advice.
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      </div>
    </>
  );
}
